import React, { ReactElement } from 'react';
import {ICustomer, ITableItem} from 'shared-types/index';
import PhoneNumberService from 'external-services/phone-number-service/index';
import { snakeCase } from 'lodash';

const characters = [
  'A',
  'B',
  'C',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'Y',
  'W',
  'Z',
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
];

const constraints = {
  _id: {
    format: {
      pattern: '^booking_[0-9][0-9][0-9][0-9]-[0-9][0-9]-[0-9][0-9]_[0-9A-Z]{13}_[0-9A-Z]{13}$',
      message: 'The booking id is not valid',
    },
    presence: false,
  },
  bookedAt: {
    presence: true,
  },
  duration: {
    presence: true,
  },
  method: {
    presence: true,
  },
  people: {
    presence: true,
  },
  time: {
    presence: true,
  },
  type: {
    presence: true,
  },
  bookingRef: {
    presence: true,
  },
};

class UtilsService {
  static rangeCheck(val: number, max: number, min: number): number {
    if (val < min) {
      val = min;
    } else if (val > max) {
      val = max;
    }
    return val;
  }

  // Maybe useful for future should takeaway mode be enabled on widget v2
  static getOrderType(isTakeAwayMode = false): string {
    return isTakeAwayMode ? 'order' : 'booking';
  }

  // https://stackoverflow.com/questions/5623838/rgb-to-hex-and-hex-to-rgb
  static hexToRgb(hex: string): number[] {
    return hex
      .replace(/^#?([a-f\d])([a-f\d])([a-f\d])$/i, (m, r, g, b) => '#' + r + r + g + g + b + b)
      .substring(1)
      .match(/.{2}/g)
      .map((x) => parseInt(x, 16));
  }

  static rgbToHex(r: number, g: number, b: number) {
    return '#' + ((1 << 24) | (r << 16) | (g << 8) | b).toString(16).slice(1);
  }
  static getRgbOpacityWithWhiteBackground(fg: number[], opacity: number): string {
    const bg = [255, 255, 255];
    const o = opacity;
    const result = fg.map((colFg, idx) => o * colFg + (1 - o) * bg[idx]);

    const hexResult = this.rgbToHex(result[0], result[1], result[2]);
    return hexResult;
  }

  static getNumberOpacityWithWhiteBackground(colorNum: number, opacity: number): string {
    const rgb = this.numberToRgb(colorNum);
    const hexResult = this.getRgbOpacityWithWhiteBackground(rgb, opacity);
    return hexResult;
  }

  static getNumberOpacityWithWhiteBackgroundAsNumber(colorNum: number, opacity: number): number {
    const hexResult = this.getNumberOpacityWithWhiteBackground(colorNum, opacity);
    return this.hexNumber(hexResult);
  }

  static hexNumber(hexString: string): number {
    return Number(hexString.replace('#', '0X'));
  }

  static numberToRgb(hexNum: number): number[] {
    return this.hexToRgb('#' + hexNum.toString(16));
  }
  static hexNumberToString(hexNumber: number): string {
    const hexString = hexNumber.toString(16).toUpperCase();
    return hexString.replace('0X', '#');
  }

  static getNumericalName(num: number) {
    if (num === null) {
      return '';
    }
    const special = [
      'zeroth',
      'first',
      'second',
      'third',
      'fourth',
      'fifth',
      'sixth',
      'seventh',
      'eighth',
      'ninth',
      'tenth',
      'eleventh',
      'twelfth',
      'thirteenth',
      'fourteenth',
      'fifteenth',
      'sixteenth',
      'seventeenth',
      'eighteenth',
      'nineteenth',
    ];
    const deca = ['twent', 'thirt', 'fort', 'fift', 'sixt', 'sevent', 'eight', 'ninet'];

    const stringifyNumber = (n: number) => {
      if (n < 20) return special[n];
      if (n % 10 === 0) return deca[Math.floor(n / 10) - 2] + 'ieth';
      return deca[Math.floor(n / 10) - 2] + 'y-' + special[n % 10];
    };
    return stringifyNumber(num + 1);
  }

  static getPhoneLinkHtml(phone: string) {
    return `<a class="underline-phone" href="tel:${phone.split(' ').join('')}">${phone}</a>`;
  }

  /**
   * Make links open in a new window when using ReactMarkdown
   * Use like this:
   * <ReactMarkdown source={text}
   renderers={{
        paragraph: 'span',
        link: UtilsService.reactMarkDownBlankTargets
      }}
   escapeHtml={false} />
   */
  static reactMarkDownBlankTargets(props: { href: string; children: string }): ReactElement {
    return (
      <a href={props.href} target="_blank" rel="noopener noreferrer">
        {props.children}
      </a>
    );
  }

  /***
   * need call start to start polling, first callback will call immediately after call start();
   * @param callback callback in interval
   * @param interval ms
   * @param count how many times want polling
   */
  static setCustomInterval(callback: any, interval = 1000, count = 100) {
    let timer: NodeJS.Timeout | number;
    let isStop = false;
    const stop = () => {
      isStop = true;
      clearTimeout(timer as NodeJS.Timeout);
    };
    const start = async () => {
      isStop = false;
      await loop();
    };
    const loop: any = async () => {
      try {
        await callback(stop);
      } catch (err) {
        console.error('Polling Error：', err);
        throw new Error('Polling Error：' + err);
      }
      if (!isStop && count > 0) {
        timer = window.setTimeout(loop, interval);
      } else {
        clearTimeout(timer as NodeJS.Timeout);
      }
      count--;
    };
    return {
      start,
      stop,
    };
  }

  /**
   * Return a single line value represents the customer main informations
   * @param {Object} customer
   * @param {boolean} showPhone
   * @returns {String} possible formats are:
   * [firstName][ lastName][, company][, phone]
   * [company][, [firstName][ lastName][, phone]
   */
  static getDisplayName(customer: ICustomer, showPhone: boolean) {
    const nameParts = [];
    if (customer.firstName && customer.firstName.trim().length > 0) {
      nameParts.push(customer.firstName);
    }
    if (customer.lastName && customer.lastName.trim().length > 0) {
      nameParts.push(customer.lastName);
    }

    const displayParts = [nameParts.join(' ').trim()];
    if (customer.company && customer.company.trim().length > 0) {
      displayParts.push(customer.company);
    }
    if (showPhone && customer.phone && customer.phone.trim().length > 0) {
      displayParts.push(PhoneNumberService.formatNumber(customer.phone, 'AU', true));
    }

    return displayParts.join(', ');
  }

  static htmlEncode(s: string): string {
    if (!s || typeof s !== 'string') {
      return s;
    }

    return s.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  static htmlDecode(s: string, isNonTextField = false) {
    if (!s || typeof s !== 'string') {
      return s;
    }
    if (isNonTextField) {
      return s
        .replace(/&amp;/g, '&')
        .replace(/&#39;/g, "'")
        .replace(/&#34;/g, '"')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>');
    } else {
      return s.replace(/&amp;/g, '&').replace(/&#39;/g, "'").replace(/&#34;/g, '"');
    }
  }

  static generateRandomCode(sizeOverride = 13): string {
    let code = '';
    while (code.length < sizeOverride) {
      code += characters[Math.floor(Math.random() * Math.floor(characters.length))];
    }
    return code;
  }

  static getResponseItemsCount(response: any): number {
    let quantity = response.headers['content-range'];
    quantity = quantity && quantity.indexOf('/') >= 0 ? quantity.split('/')[1] : '0';
    return parseInt(quantity, null);
  }

  static changeObjectKeyFromCamelToSnakeCase(obj: { [key: string]: string }): {
    [key: string]: string;
  } {
    const newObj: { [key: string]: string } = {};
    Object.keys(obj).forEach((key) => {
      newObj[snakeCase(key)] = obj[key];
    });
    return newObj;
  }

  static isKeyExistAndValid(key: string, data: Object, isValidCheck: boolean = false): boolean {
    const isKeyExist: boolean = key in data;
    if (!isValidCheck) return isKeyExist;

    if ((constraints as any)[key].presence) {
      return !!(data as any)[key];
    }

    if ((data as any)[key] && (constraints as any)[key].format) {
      const _regExp = new RegExp((constraints as any)[key].format.pattern);
      const valid = _regExp.exec((data as any)[key]);
      return !!valid;
    } else {
      return true;
    }
  }

  static validateBookingObjectByConstraints(booking: any): any {
    const keys = Object.keys(constraints);
    const validArr: any = [];
    keys.forEach((key: string) => {
      const isValid = this.isKeyExistAndValid(key, booking, true);
      !isValid &&
        validArr.push({
          [key]: 'Is not Valid',
        });
    });
    return validArr.length ? validArr : false;
  }

  /**
   * Based losely on uuidv4 above, but shorter and just for css class name prefixes.
   * First character must be a letter, so using letter "y" just for sake of it.
   */
  static uniqueCssClassPrefix() {
    return 'yxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : c;
      return v.toString(16);
    });
  }
  // for sort multiple fields, set to empty string if it is null. if compare number, please add number type.
  static fieldSorter = (fields: string[]) => (a: any, b: any) =>
    fields
      .map((o) => {
        const previous = a[o] || '';
        const next = b[o] || '';
        let dir = 1;
        if (o[0] === '-') {
          dir = -1;
          o = o.substring(1);
        }
        // move empty string to the end, if you want move to top, use comment out code under this if statement
        if (previous > next) {
          return !next ? -dir : dir;
        } else if (previous < next) {
          return !previous ? dir : -dir;
        } else {
          return 0;
        }
        // return previous > next ? dir : previous < next ? -(dir) : 0;
      })
      .reduce((p, n) => (p ? p : n), 0);

  static alphaSortFilter(array: ITableItem[], sortOn: string) {
    function sortAlphaNum(a: any, b: any) {
      if (sortOn) {
        a = a[sortOn];
        b = b[sortOn];
      }

      return a.toString().localeCompare(b.toString(), undefined, { numeric: true });
    }

    return array.sort(sortAlphaNum);
  };
  static getFullName(firstName?: string | null, lastName?: string | null): string {
    const first = (firstName || '').trim();
    const last = (lastName || '').trim();
    return `${first} ${last}`.trim();
  }
}
export default UtilsService;
